import EPageHeader from '@/components/everridge/EPageHeader';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SettingsFormSchema } from '@/features/settings/validators/SettingsValidator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import SettingsCompanyDetails from '@/features/settings/components/SettingsCompanyDetails';
import SettingsBillingDetails from '@/features/settings/components/SettingsBillingDetails';
import SettingsColor from '@/features/settings/components/SettingsColor';
import SettingsLogo from '@/features/settings/components/SettingsLogo';
import { useEffect } from 'react';
import { useSettingsApi } from '@/features/settings/hooks/useSettingsApi';
import { UpdateSettingsDto } from '@/features/settings/types/settings-dto';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { ColorsHelper } from '@/utils/ColorsHelper';
import SettingsBookingDetails from '@/features/settings/components/SettingsBookingDetails';
import SettingsRentAnalyticsExpenses from '@/features/settings/components/SettingsRentAnalyticsExpenses';
import SettingsPaymentDetails from '@/features/settings/components/SettingsPaymentDetails';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

export type SettingsFormData = z.infer<typeof SettingsFormSchema>;

const SettingsPage = () => {
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();

    const { settingsData, updateSettings, uploadSettingsLogo } = useSettingsApi();
    const { setOrdersEnabled } = useMultiCurrencyStorage();

    const { control, handleSubmit, reset, setValue } = useForm<SettingsFormData>({
        resolver: zodResolver(SettingsFormSchema),
        defaultValues: {
            rentAnalyticsHiddenExpenseKeys: [],
        },
    });

    useEffect(() => {
        if (settingsData) {
            if (settingsData.primaryColor) {
                ColorsHelper.setNewColor(settingsData.primaryColor, settingsData.darkMode);
            }

            reset({
                companyName: settingsData.companyName,
                companyTin: settingsData.companyTin,
                legalAddress: settingsData.legalAddress,
                phoneNumber: settingsData.phoneNumber ?? '995',
                website: settingsData.website,
                bankName: settingsData.bankName,
                swiftCode: settingsData.swiftCode,
                iban: settingsData.iban,
                primaryColor: settingsData.primaryColor,
                darkMode: settingsData.darkMode,
                logo: settingsData.logoUrl,
                hotelName: settingsData.hotelName,
                reservationDaysBeforeToCancel: settingsData.reservationDaysBeforeToCancel,
                orderDaysBeforeToCancel: settingsData.orderDaysBeforeToCancel,
                reservationDaysBeforeToEdit: settingsData.reservationDaysBeforeToEdit,
                orderDaysBeforeToEdit: settingsData.orderDaysBeforeToEdit,
                multiCurrency: settingsData.multiCurrency ?? false,
                ordersEnabled: settingsData.ordersEnabled ?? true,
                rentAnalyticsHiddenExpenseKeys: [
                    ...(settingsData.rentAnalyticsHiddenExpenseKeys ?? []),
                ],
            });
        }

        return () => {
            const setColor =
                document.documentElement.style.getPropertyValue('--color-primary-color');

            const setDarkMode =
                document.documentElement.style.getPropertyValue('--color-primary-contarst') ===
                'black'
                    ? true
                    : false;

            if (
                settingsData?.primaryColor &&
                (settingsData?.primaryColor !== setColor || settingsData?.darkMode !== setDarkMode)
            ) {
                ColorsHelper.setNewColor(settingsData.primaryColor, settingsData.darkMode);
            }
        };
    }, [settingsData, reset]);

    const handleFormSubmit = async (form: SettingsFormData) => {
        const dto: UpdateSettingsDto = {
            companyName: form.companyName,
            companyTin: form.companyTin,
            legalAddress: form.legalAddress,
            phoneNumber: form.phoneNumber,
            website: form.website,
            bankName: form.bankName,
            swiftCode: form.swiftCode,
            iban: form.iban,
            primaryColor: form.primaryColor,
            darkMode: form.darkMode,
            hotelName: form.hotelName,
            reservationDaysBeforeToCancel: form.reservationDaysBeforeToCancel,
            orderDaysBeforeToCancel: form.orderDaysBeforeToCancel,
            reservationDaysBeforeToEdit: form.reservationDaysBeforeToEdit,
            orderDaysBeforeToEdit: form.orderDaysBeforeToEdit,
			multiCurrency: form.multiCurrency,
			ordersEnabled: form.ordersEnabled,
            rentAnalyticsHiddenExpenseKeys: form.rentAnalyticsHiddenExpenseKeys,
        };

        await updateSettings(dto);
        setOrdersEnabled(form.ordersEnabled);

        if (form.logo instanceof File) {
            await uploadSettingsLogo(form.logo);
        }
    };
    
    return (
        <section className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={t('main.settings.title')}>
                <Button onClick={handleSubmit(handleFormSubmit)}>
                    {t('elements.buttons.saveChanges')}
                </Button>
            </EPageHeader>
            <div className="flex flex-col gap-4">
                <div className={cn('flex gap-4', isMobile && 'flex-col')}>
                    <SettingsCompanyDetails control={control} isMobile={isMobile} />
                    <div className={cn('w-1/2 flex flex-col gap-4', isMobile && 'w-full')}>
                        <SettingsBillingDetails control={control} isMobile={isMobile} />
                        <SettingsBookingDetails control={control} isMobile={isMobile} />
                    </div>
                </div>
                <div className={cn('flex gap-4', isMobile && 'flex-col')}>
                    <SettingsColor control={control} setValue={setValue} isMobile={isMobile} />
                    <SettingsLogo control={control} isMobile={isMobile} />
                </div>
                <SettingsRentAnalyticsExpenses
                    control={control}
                    isMobile={isMobile}
                    canConfigure={
                        settingsData?.allowTenantAdminRentAnalyticsExpenseVisibility ?? false
                    }
                />
                <SettingsPaymentDetails isMobile={isMobile} />
            </div>
        </section>
    );
};

export default SettingsPage;
